import React, { useState, useEffect } from 'react';
import { Database, Trash2, BarChart3, RefreshCw } from 'lucide-react';
import { getCacheStats, clearAllCache, cleanupExpiredCache } from '../../services/tmdbCache';

const TMDBCacheSettings = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  const loadStats = async () => {
    try {
      const cacheStats = await getCacheStats();
      setStats(cacheStats);
      setLastUpdated(new Date().toLocaleTimeString());
    } catch (error) {
      console.error('Error loading cache stats:', error);
    }
  };

  useEffect(() => {
    loadStats();
  }, []);

  const handleClearCache = async () => {
    if (!window.confirm('Clear all TMDB cache data? This will force fresh API calls for all movies.')) {
      return;
    }

    setLoading(true);
    try {
      const success = await clearAllCache();
      if (success) {
        alert('✅ TMDB cache cleared successfully!');
        await loadStats();
      } else {
        alert('❌ Failed to clear cache. Please try again.');
      }
    } catch (error) {
      console.error('Error clearing cache:', error);
      alert('❌ Error clearing cache. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCleanupExpired = async () => {
    setLoading(true);
    try {
      await cleanupExpiredCache();
      alert('✅ Expired cache entries cleaned up!');
      await loadStats();
    } catch (error) {
      console.error('Error cleaning up cache:', error);
      alert('❌ Error cleaning up cache. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-gray-800 border-2 border-orange-600 rounded-lg shadow-2xl p-6">
      <div className="flex items-center gap-3 mb-6">
        <Database className="w-6 h-6 text-orange-400" />
        <h2 className="text-xl font-bold text-orange-300">TMDB Cache Management</h2>
      </div>

      <div className="space-y-6">
        {/* Cache Statistics */}
        <div className="bg-gray-700 p-4 rounded-lg">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-semibold text-orange-200 flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              Cache Statistics
            </h3>
            <button
              onClick={loadStats}
              disabled={loading}
              className="flex items-center gap-2 px-3 py-1 bg-blue-600 hover:bg-blue-500 disabled:bg-gray-600 text-white rounded-md text-sm transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
          
          {stats ? (
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gray-800 p-3 rounded">
                <div className="text-2xl font-bold text-green-400">{stats.memorySize}</div>
                <div className="text-sm text-gray-300">Items in Memory</div>
                <div className="text-xs text-gray-400">Max: {stats.maxMemorySize}</div>
              </div>
              <div className="bg-gray-800 p-3 rounded">
                <div className="text-2xl font-bold text-blue-400">{stats.supabaseSize}</div>
                <div className="text-sm text-gray-300">Items in Database</div>
                <div className="text-xs text-gray-400">TTL: {stats.ttlHours}h</div>
              </div>
            </div>
          ) : (
            <div className="text-gray-400">Loading cache statistics...</div>
          )}
          
          {lastUpdated && (
            <div className="text-xs text-gray-500 mt-2">
              Last updated: {lastUpdated}
            </div>
          )}
        </div>

        {/* Cache Management Actions */}
        <div className="bg-gray-700 p-4 rounded-lg">
          <h3 className="text-lg font-semibold text-orange-200 mb-3">Cache Management</h3>
          
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium text-gray-200">Clean Up Expired Entries</div>
                <div className="text-sm text-gray-400">Remove cache entries older than 7 days</div>
              </div>
              <button
                onClick={handleCleanupExpired}
                disabled={loading}
                className="flex items-center gap-2 px-4 py-2 bg-yellow-600 hover:bg-yellow-500 disabled:bg-gray-600 text-white rounded-md transition-colors"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                Cleanup
              </button>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium text-gray-200">Clear All Cache</div>
                <div className="text-sm text-gray-400">Remove all cached TMDB data (forces fresh API calls)</div>
              </div>
              <button
                onClick={handleClearCache}
                disabled={loading}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-500 disabled:bg-gray-600 text-white rounded-md transition-colors"
              >
                <Trash2 className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                Clear All
              </button>
            </div>
          </div>
        </div>

        {/* Cache Information */}
        <div className="bg-gray-700 p-4 rounded-lg">
          <h3 className="text-lg font-semibold text-orange-200 mb-3">How Caching Works</h3>
          <div className="text-sm text-gray-300 space-y-2">
            <p>
              <strong>Memory Cache:</strong> Fast in-memory storage for immediate access during the current session.
            </p>
            <p>
              <strong>Database Cache:</strong> Persistent storage in Supabase that survives page reloads and deployments.
            </p>
            <p>
              <strong>Cache Duration:</strong> TMDB data is cached for 7 days to balance freshness with performance.
            </p>
            <p>
              <strong>Benefits:</strong> Reduces API calls, improves loading speed, and provides better user experience.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TMDBCacheSettings;

